import { Box, Switch, TextField, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { GET_ADMIN_CONSOLE_OPS_AVAILABILITY_RESTRICTION } from './admin-console-operations.gql';
import { GetAdminConsoleOpsAvailabilityRestriction } from './__generated__/GetAdminConsoleOpsAvailabilityRestriction';

export function AvailabilityRestriction() {
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const { theme } = useCampfireTheme();
  const campfireMutation = useCampfireFetch({ defer: true });

  const { data, refetch } = useCampfireQuery<GetAdminConsoleOpsAvailabilityRestriction, undefined>(
    GET_ADMIN_CONSOLE_OPS_AVAILABILITY_RESTRICTION
  );

  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState<number | ''>('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const serverDays = data?.tm.tenant?.config?.availabilityRestrictionDays;
    if (serverDays !== undefined && serverDays !== null) {
      setEnabled(true);
      setDays(serverDays);
    } else {
      setEnabled(false);
      setDays('');
    }
    setHasChanges(false);
  }, [data]);

  const handleToggle = () => {
    setEnabled(!enabled);
    if (enabled) {
      setDays('');
    }
    setHasChanges(true);
  };

  const handleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setDays('');
    } else {
      const numValue = parseInt(e.target.value, 10);
      if (!Number.isNaN(numValue)) {
        setDays(numValue);
      }
    }
    setHasChanges(true);
  };

  const handleSave = () => {
    const valueToSave = enabled && days !== '' ? days : null;

    campfireMutation
      .run({
        url: `${apiUrl}/tm/config/availability-restriction`,
        method: 'post',
        data: {
          days: valueToSave,
        },
      })
      .then(() => {
        if (refetch) {
          setTimeout(refetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Availability restriction updated',
        });
        setHasChanges(false);
      })
      .catch(() => {
        setSnackbar({
          variant: 'error',
          open: true,
          message: 'Failed to update availability restriction',
        });
      });
  };

  const isValidDays = days === '' || (typeof days === 'number' && days >= 7 && days <= 365);
  const canSave = hasChanges && (!enabled || (enabled && days !== '' && isValidDays));

  return (
    <Box mt={2}>
      <Box display='flex' alignItems='center'>
        <Switch
          checked={enabled}
          onChange={handleToggle}
          color='primary'
          inputProps={{ 'aria-label': 'enable availability restriction' }}
        />
        <Box ml={1}>
          <Typography
            style={{
              fontWeight: 500,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Restrict Availability Submission
          </Typography>
          <Typography variant='body2' style={{ fontSize: '13px', color: '#808080' }}>
            Limit how far in advance volunteers can submit availability
          </Typography>
        </Box>
      </Box>

      {enabled && (
        <Box mt={3} ml={7}>
          <Typography style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
            Maximum Days in Advance
          </Typography>
          <Typography variant='body2' style={{ fontSize: '13px', color: '#808080', marginBottom: '16px' }}>
            Volunteers will only be able to submit availability for dates within this window. Set between 7 and 365
            days.
          </Typography>
          <TextField
            type='number'
            value={days}
            onChange={handleDaysChange}
            variant='outlined'
            label='Days'
            placeholder='e.g. 30'
            inputProps={{ min: 7, max: 365 }}
            style={{ width: 150 }}
            error={days !== '' && !isValidDays}
            helperText={days !== '' && !isValidDays ? 'Must be between 7 and 365' : ''}
          />
        </Box>
      )}

      {hasChanges && (
        <Box display='flex' justifyContent='flex-end' mt={3}>
          <ConsoleActionButton
            variant='contained'
            color='primary'
            onClick={handleSave}
            disabled={!canSave}
            style={{
              backgroundColor: theme.color.secondary.main900,
              color: '#ffffff',
              boxSizing: 'border-box',
              borderRadius: '4px',
              textTransform: 'none',
              padding: '7px 12px',
              minWidth: '40px',
            }}
          >
            Save
          </ConsoleActionButton>
        </Box>
      )}
    </Box>
  );
}
